import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Upload, Save, Loader2, Search } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { AdminTabs } from "../components/admin/AdminTabs";
import { SheetLinkCard } from "../components/admin/SheetLinkCard";
import { PriceTable } from "../components/admin/PriceTable";
import { AddProductDialog } from "../components/admin/AddProductDialog";
import { RapidPriceTable } from "../components/admin/RapidPriceTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/prices`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("ib_access_token")}` });

export default function PriceSheetPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(API, { headers: authHeaders() });
      setData(data);
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load price sheet");
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    load();
  }, [user, loading, navigate, load]);

  const setEdit = (key, field, value) => setEdits((e) => ({ ...e, [key]: { ...e[key], [field]: value } }));
  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    const products = [];
    const market = [];
    const rapid = [];
    Object.entries(edits).forEach(([key, v]) => {
      if (key.startsWith("m:")) market.push({ symbol: key.slice(2), price: Number(v.price) });
      else if (key.startsWith("r:")) rapid.push({ id: key.slice(2), price_ncr: v.price_ncr !== undefined ? Number(v.price_ncr) : undefined, price_pyg: v.price_pyg !== undefined ? Number(v.price_pyg) : undefined });
      else products.push({ id: key.slice(2), price: v.price !== undefined ? Number(v.price) : undefined, quantity_available: v.quantity_available !== undefined ? Number(v.quantity_available) : undefined });
    });
    setSaving(true);
    try {
      const { data } = await axios.put(API, { products, market, rapid }, { headers: authHeaders() });
      toast.success(`Saved ${data.updated} price update${data.updated === 1 ? "" : "s"} — live on the site now`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    const res = await axios.get(`${API}/export`, { headers: authHeaders(), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "ironbyte_price_sheet.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/import`, form, { headers: authHeaders() });
      toast.success(`Imported: ${data.updated} updated, ${data.skipped} skipped`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeProduct = async (id) => {
    try {
      await axios.delete(`${API}/products/${id}`, { headers: authHeaders() });
      toast.success("Product removed from the marketplace");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  if (loading || !user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen pt-28 pb-20 px-6" data-testid="price-sheet-page">
      <div className="max-w-7xl mx-auto">
        <AdminTabs active="prices" />
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">Admin</span>
            <h1 className="font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight mt-2 text-ib-text">Daily Price Sheet</h1>
            <p className="font-manrope text-sm text-ib-text-secondary mt-2">Edit rates below, upload a CSV/Excel, or link a Google Sheet. Every saved change is instantly visible on the marketplace and ticker. All rates in ₹/MT.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <AddProductDialog api={API} headers={authHeaders} onAdded={load} />
            <Button data-testid="download-sheet-btn" onClick={download} variant="outline" size="sm" className="border-ib-steel/40 text-ib-text-secondary hover:text-ib-blue font-outfit text-xs gap-1.5"><Download className="w-3.5 h-3.5" /> Download CSV</Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="upload-sheet-input" onChange={(e) => upload(e.target.files?.[0])} />
            <Button data-testid="upload-sheet-btn" onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" size="sm" className="border-ib-steel/40 text-ib-text-secondary hover:text-ib-lime font-outfit text-xs gap-1.5">
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload CSV / Excel
            </Button>
            <Button data-testid="save-prices-btn" onClick={save} disabled={saving || dirtyCount === 0} size="sm" className="bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit text-xs gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
            </Button>
          </div>
        </div>

        <SheetLinkCard initialUrl={data?.sheet_url || ""} lastSync={data?.last_sync} onSynced={load} api={API} headers={authHeaders} />

        <div className="relative mb-4 mt-8 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ib-text-muted" />
          <Input data-testid="price-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter products by name, category, supplier..." className="pl-9 bg-ib-surface border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-manrope text-sm" />
        </div>

        {data && (
          <>
            <PriceTable
              market={data.market}
              products={data.products.filter((p) => `${p.name} ${p.category} ${p.supplier} ${p.grade}`.toLowerCase().includes(q.toLowerCase()))}
              edits={edits}
              onEdit={setEdit}
              onDelete={removeProduct}
            />
            <div className="mt-10">
              <RapidPriceTable rapid={data.rapid || []} edits={edits} onEdit={setEdit} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
